import { CarrierConfigResponse } from '@api/carrier'
import { SettingsResponse } from '@api/settings'
import config from '@config'
import { initialColors } from '@lib/style'
import { TicketsAction } from '@queries/settings/enum'
import { initFilter } from '@stores/settings'

const {
  fallback: { currency, locale },
} = config

export const settingsInitialData: SettingsResponse = {
  priceHints: { enabled: false },
  discountVoucher: { enabled: false },
  backgroundImage: { enabled: false, url: null, headerOffset: false },
  bookingDetails: { showFareClass: false },
  bookingNumbers: {
    carrierNumber: false,
    distribusionNumber: true,
    retailerNumber: false,
  },
  bookedOut: { enabled: false },
  checkout: { type: 'old', merchantName: 'Distribusion Technologies' },
  checkoutPassengerEdit: { allowCount: true, allowType: true },
  loadConnections: {
    enabled: false,
    timeAllowance: 0,
  },
  colors: initialColors,
  currency: { supported: currency.supported, default: currency.default },
  fareClasses: { displayOn: 'nowhere', supported: [], split: false },
  filter: initFilter,
  locale: { supported: locale.supported, default: locale.default },
  passengerType: { enabled: false },
  returnStartTime: { enabled: false },
  returnEndTime: { enabled: false },
  returnTrip: { enabled: false, mixedFareClasses: false },
  searchBar: { layout: 'column' },
  searchResultUrl: { url: config.searchResultUrl },
  seatSelection: {
    showFareFeatures: false,
    enabledOnSearchResults: false,
    enabledOnCheckout: false,
    flowType: 'single_step',
    sendFareClass: false,
    showPrice: true,
    mandatory: false,
    showSeatsPerSegmentOnCheckout: false,
    preselectSeats: false,
  },
  sorting: { enabled: false, supported: [], default: null },
  title: { text: '', favicon: '' },
  tripTime: { enabled: false },
  cpf: { enabled: false },
  paymentProvidersMethods: {
    applePay: null,
    blik: null,
    cash: null,
    checkoutcomFlow: null,
    creditCard: null,
    mp: null,
    googlePay: null,
    invoice: null,
    ideal: null,
    dana: null,
    gcash: null,
    paypal: null,
    pix: null,
    storedPayment: null,
    terminal: null,
    ox: null,
    sepa_direct_debit: null,
  },
  reservation: { enabled: false },
  reservationCardCheck: { enabled: false },
  ancillaries: { enabled: false, preselected: [] },
  passengerTypesList: {
    enabled: false,
    carrier: null,
    multiple: false,
    ageRanges: [],
    crossCountry: false,
    totalPaxLimit: null,
  },
  quickReservation: { enabled: false },
  discountCodes: { enabled: false, displayOn: 'everywhere' },
  amtrackSavedCards: { enabled: false, cvc: false },
  paxDiscountCodes: { enabled: false, displayOn: 'everywhere' },
  billingAddress: { enabled: false },
  carrierLogo: { enabled: true, source: 'marketing_carrier' },
  holdBooking: { enabled: false, copyLink: true },
  downloadTicket: { enabled: false, action: TicketsAction.DOWNLOAD },
  marketingMaterials: { enabled: false },
  searchCalendar: { enabled: true, displayPrice: 'all_days' },
  vehicleIdentifier: { enabled: false },
  email: { enabled: true, optional: false, verification: false },
  endpoints: {
    newBackend: [],
  },
  whatsappButton: {
    enabled: false,
    phoneNumber: null,
  },
  gender: {
    enabled: false,
    trips: 'all',
  },
  customElements: {
    enabled: false,
    pages: [],
  },
  manageBookings: { enabled: false, backgroundUrl: null },
  nethone: { enabled: false },
  trackTrip: { enabled: false },
  menu: { items: [] },
  priceCalendar: { enabled: false },
  accommodation: { enabled: false, partner: 'expedia' },
  footer: { menu: [], socials: [] },
  support: { url: null },
  agentCounterPage: { departureLocation: null, arrivalLocation: null },
  customDomain: { enabled: false },
  governmentId: {
    trips: 'all',
    types: [],
    default: null,
    fields: [],
  },
  logo: { url: null, footerUrl: null, redirectionUrl: null },
  transportationMode: { enabled: false, supported: [] },
  suggestions: { count: 10, display: ['station', 'area', 'city'] },
  media: { enabled: false, displayOn: 'everywhere', preview: false },
  reselling: { enabled: false, image: null, link: null },
  passengerName: { enabled: true },
  termsAndPrivacy: { enabled: true },
  seo: { enabled: true },
  distribusionBranding: { enabled: false, pages: [], displayOn: 'everywhere' },
  cookiesConsent: {
    enabled: false,
    provider: null,
    configuration: null,
  },
  changeCalculator: { enabled: false },
  abTest: { enabled: false, feature: null },
  invoicing: { enabled: false },
  weeklyTrip: { enabled: false, searchDays: 7, requiredDays: 5, returnTrip: true },
  seatProduct: { enabled: false },
  journeyCard: { view: 'detailed' },
  resendConfirmationEmail: { enabled: false },
  passengerDetails: { dynamicPassengerData: false, mandatory: true },
  manageBookingHelpBanner: { enabled: false, email: null },
  userInterface: {
    date: { textInputEnabled: false },
    time: { textInputEnabled: false },
  },
  stopOverFilter: {
    default: 'include',
    enabled: true,
    ruleSelection: false,
  },
  advancedSearch: {
    enabled: false,
    default: 'simple',
    travelClass: false,
    minTransferTime: {
      enabled: false,
      intervalMinutes: 0,
      intervalCount: 1,
    },
    trainType: false,
  },
  openReturn: { enabled: false },
  multistepCheckout: { enabled: false },
  subscription: { cancellationEnabled: false },
}

export const carrierInitialData: CarrierConfigResponse = {
  googleWallet: { enabled: false },
  boardingByQrCode: { enabled: false },
  phone: { enabled: false, optional: false },
  birthday: { enabled: false },
  governmentId: { enabled: false },
  logo: { url: '' },
  cpf: { perPassenger: false },
  passengerTypesList: {
    totalPaxLimit: null,
  },
}
